/**
 * Tree helpers for the Smart Mock "Schemas" section.
 *
 * Schemas live in the workspace snapshot under a single `{ folders, items }` shape
 * (workspace-wide, mirroring how a collection holds `settings.folders` + `requests`):
 *   folders: [ { id, name, parent_id|null, sort_order } ]
 *   items:   [ SchemaItem ]   // a SchemaItem is a leaf that opens the table editor
 *
 * A SchemaItem carries a recursive field tree:
 *   { id, name, folder_id|null, sort_order, rootType:'object'|'array', nodes:[SchemaNode] }
 *   SchemaNode = { id, name, type, value, refSchemaId|null, description, children:[SchemaNode] }
 *
 * Folder helpers reuse the generic logic from collectionFolders.js.
 */

import {
  normalizeParentId,
  isDescendantFolder,
  collectFolderSubtreeIds,
  cloneFolderSubtree,
} from './collectionFolders';

export const SCHEMA_NODE_TYPES = [
  'string',
  'integer',
  'number',
  'boolean',
  'object',
  'array',
  'null',
  'ref',
];

export function generateSchemaId() {
  return `sch-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
export function generateSchemaFolderId() {
  return `schfld-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
export function generateSchemaNodeId() {
  return `n-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function emptySchemas() {
  return { folders: [], items: [] };
}

/** Ensure a valid `{ folders, items }` shape from arbitrary persisted input. */
export function normalizeSchemas(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return emptySchemas();
  return {
    folders: Array.isArray(raw.folders) ? raw.folders.map((f) => ({ ...f })) : [],
    items: Array.isArray(raw.items) ? raw.items.map(normalizeSchemaItem) : [],
  };
}

export function normalizeSchemaItem(item) {
  if (!item || typeof item !== 'object') return item;
  return {
    id: item.id || generateSchemaId(),
    name: item.name || 'New Schema',
    folder_id: normalizeParentId(item.folder_id),
    sort_order: typeof item.sort_order === 'number' ? item.sort_order : 0,
    rootType: item.rootType === 'array' ? 'array' : 'object',
    nodes: Array.isArray(item.nodes) ? item.nodes.map(normalizeSchemaNode) : [],
  };
}

export function normalizeSchemaNode(node) {
  if (!node || typeof node !== 'object') return makeSchemaNode();
  const type = SCHEMA_NODE_TYPES.includes(node.type) ? node.type : 'string';
  const hasChildren = type === 'object' || type === 'array';
  const enumValues = Array.isArray(node.enum)
    ? node.enum.map(String).filter(Boolean)
    : Array.isArray(node.enumValues)
      ? node.enumValues.map(String).filter(Boolean)
      : [];
  return {
    id: node.id || generateSchemaNodeId(),
    name: typeof node.name === 'string' ? node.name : '',
    type,
    value: typeof node.value === 'string' ? node.value : '',
    refSchemaId: node.refSchemaId ?? null,
    description: typeof node.description === 'string' ? node.description : '',
    enum: enumValues.length ? enumValues : undefined,
    minItems: typeof node.minItems === 'number' ? node.minItems : undefined,
    maxItems: typeof node.maxItems === 'number' ? node.maxItems : undefined,
    children: hasChildren && Array.isArray(node.children)
      ? node.children.map(normalizeSchemaNode)
      : [],
  };
}

export function makeSchemaNode(type = 'string') {
  return {
    id: generateSchemaNodeId(),
    name: '',
    type,
    value: '',
    refSchemaId: null,
    description: '',
    children: [],
  };
}

export function nodeHasChildren(node) {
  return node?.type === 'object' || node?.type === 'array';
}

// ---------------------------------------------------------------------------
// Tree (folders + items)
// ---------------------------------------------------------------------------

/** Ordered siblings (folders + schema items) at a parent (null = root). */
export function getOrderedSchemaChildren(schemas, parentId = null) {
  const s = normalizeSchemas(schemas);
  const pid = normalizeParentId(parentId);
  const folders = s.folders
    .filter((f) => normalizeParentId(f.parent_id) === pid)
    .map((f) => ({ type: 'folder', item: f, id: f.id, sort_order: numOrder(f) }));
  const items = s.items
    .filter((it) => normalizeParentId(it.folder_id) === pid)
    .map((it) => ({ type: 'schema', item: it, id: it.id, sort_order: numOrder(it) }));
  return [...folders, ...items].sort((a, b) => a.sort_order - b.sort_order);
}

function numOrder(x) {
  return typeof x?.sort_order === 'number' ? x.sort_order : 0;
}

function nextSortOrder(schemas, parentId) {
  const siblings = getOrderedSchemaChildren(schemas, parentId);
  return siblings.reduce((m, s) => Math.max(m, s.sort_order), -1) + 1;
}

function reindexSchemaSiblings(schemas, parentId, orderedEntries) {
  const pid = normalizeParentId(parentId);
  const orderMap = new Map(orderedEntries.map((entry, index) => [String(entry.id), index]));
  return {
    folders: schemas.folders.map((f) =>
      orderMap.has(String(f.id))
        ? { ...f, sort_order: orderMap.get(String(f.id)), parent_id: pid }
        : f
    ),
    items: schemas.items.map((it) =>
      orderMap.has(String(it.id))
        ? { ...it, sort_order: orderMap.get(String(it.id)), folder_id: pid }
        : it
    ),
  };
}

/** Place `entryId` immediately after `sourceId` among siblings under `parentId`. */
function positionSchemaEntryAfterSource(
  schemas,
  parentId,
  sourceKind,
  sourceId,
  entryKind,
  entryId
) {
  const pid = normalizeParentId(parentId);
  const sourceType = sourceKind === 'folder' ? 'folder' : 'schema';
  const siblings = getOrderedSchemaChildren(schemas, pid);
  const without = siblings.filter((s) => String(s.id) !== String(entryId));
  const sourceIdx = without.findIndex(
    (s) => s.type === sourceType && String(s.id) === String(sourceId)
  );
  const insertAt = sourceIdx === -1 ? without.length : sourceIdx + 1;
  const reordered = [
    ...without.slice(0, insertAt).map((s) => ({ kind: s.type, id: s.id })),
    { kind: entryKind, id: entryId },
    ...without.slice(insertAt).map((s) => ({ kind: s.type === 'folder' ? 'folder' : 'schema', id: s.id })),
  ];
  return reindexSchemaSiblings(schemas, pid, reordered);
}

export function createSchema(schemas, { name = 'New Schema', folder_id = null } = {}) {
  const s = normalizeSchemas(schemas);
  const item = {
    id: generateSchemaId(),
    name,
    folder_id: normalizeParentId(folder_id),
    sort_order: nextSortOrder(s, folder_id),
    rootType: 'object',
    nodes: [makeSchemaNode('string')],
  };
  return { schemas: { ...s, items: [...s.items, item] }, item };
}

export function createSchemaFolder(schemas, { name = 'New Folder', parent_id = null } = {}) {
  const s = normalizeSchemas(schemas);
  const folder = {
    id: generateSchemaFolderId(),
    name,
    parent_id: normalizeParentId(parent_id),
    sort_order: nextSortOrder(s, parent_id),
  };
  return { schemas: { ...s, folders: [...s.folders, folder] }, folder };
}

export function renameSchemaEntry(schemas, kind, id, name) {
  const s = normalizeSchemas(schemas);
  if (kind === 'folder') {
    return {
      ...s,
      folders: s.folders.map((f) => (String(f.id) === String(id) ? { ...f, name } : f)),
    };
  }
  return {
    ...s,
    items: s.items.map((it) => (String(it.id) === String(id) ? { ...it, name } : it)),
  };
}

export function updateSchemaItem(schemas, id, patch) {
  const s = normalizeSchemas(schemas);
  return {
    ...s,
    items: s.items.map((it) => (String(it.id) === String(id) ? { ...it, ...patch } : it)),
  };
}

export function getSchemaItem(schemas, id) {
  return normalizeSchemas(schemas).items.find((it) => String(it.id) === String(id)) ?? null;
}

/**
 * Overlay open schema editor tabs onto the saved schemas tree so unsaved edits
 * are visible to the mock server (mirrors collectionsWithTabMockOverlay).
 */
export function schemasWithTabOverlay(schemas, schemaTabs = []) {
  const base = normalizeSchemas(schemas);
  if (!schemaTabs?.length) return base;

  const items = base.items.map((item) => {
    const tab = schemaTabs.find(
      (t) => t?.schemaId != null && String(t.schemaId) === String(item.id)
    );
    if (!tab || !tab.hasUnsavedChanges) return item;
    return {
      ...item,
      name: tab.name ?? item.name,
      rootType: tab.rootType === 'array' ? 'array' : 'object',
      nodes: Array.isArray(tab.nodes) ? tab.nodes : item.nodes,
    };
  });

  return { ...base, items };
}

export function deleteSchemaEntry(schemas, kind, id) {
  const s = normalizeSchemas(schemas);
  if (kind === 'folder') {
    const removeIds = collectFolderSubtreeIds(s.folders, id);
    return {
      folders: s.folders.filter((f) => !removeIds.has(String(f.id))),
      items: s.items.filter((it) => !removeIds.has(String(it.folder_id))),
    };
  }
  return { ...s, items: s.items.filter((it) => String(it.id) !== String(id)) };
}

export function duplicateSchemaItem(schemas, id) {
  const s = normalizeSchemas(schemas);
  const src = getSchemaItem(s, id);
  if (!src) return s;
  const copy = {
    ...JSON.parse(JSON.stringify(src)),
    id: generateSchemaId(),
    name: `${src.name || 'Schema'} Copy`,
    sort_order: src.sort_order ?? 0,
  };
  // fresh node ids to avoid collisions
  copy.nodes = remapNodeIds(copy.nodes);
  let next = { ...s, items: [...s.items, copy] };
  return positionSchemaEntryAfterSource(
    next,
    src.folder_id,
    'schema',
    id,
    'schema',
    copy.id
  );
}

/**
 * Duplicate a schema folder together with its entire subtree (nested folders +
 * schema items), assigning fresh ids throughout. The copy is placed immediately
 * after the original under the same parent.
 */
export function duplicateSchemaFolder(schemas, id) {
  const s = normalizeSchemas(schemas);
  const src = s.folders.find((f) => String(f.id) === String(id));
  if (!src) return s;

  const { copiedFolders, idMap, subtreeIds, rootId } = cloneFolderSubtree(s.folders, id, {
    newFolderId: generateSchemaFolderId,
    rootSortOrder: src.sort_order ?? 0,
  });

  const newItems = s.items
    .filter((it) => subtreeIds.has(String(normalizeParentId(it.folder_id))))
    .map((it) => ({
      ...JSON.parse(JSON.stringify(it)),
      id: generateSchemaId(),
      folder_id: idMap.get(String(normalizeParentId(it.folder_id))),
      nodes: remapNodeIds(it.nodes),
    }));

  let next = {
    folders: [...s.folders, ...copiedFolders],
    items: [...s.items, ...newItems],
  };
  return positionSchemaEntryAfterSource(
    next,
    src.parent_id,
    'folder',
    id,
    'folder',
    rootId
  );
}

function remapNodeIds(nodes) {
  return (nodes || []).map((n) => ({
    ...n,
    id: generateSchemaNodeId(),
    children: remapNodeIds(n.children),
  }));
}

/** Parse a schema-sidebar drag id (`folder-*` | `schema-*`). */
export function parseSchemaDndId(raw) {
  if (!raw) return null;
  if (raw.startsWith('folder-')) return { kind: 'folder', id: raw.slice(7) };
  if (raw.startsWith('schema-')) return { kind: 'schema', id: raw.slice(7) };
  return null;
}

/**
 * Validate a prospective schema-tree drop and return `{ overId, position }`, or null
 * when the drop is not legal (so no indicator is drawn).
 */
export function computeSchemaDropIndicator(schemas, activeId, overId, position) {
  const activeP = parseSchemaDndId(activeId);
  const overP = parseSchemaDndId(overId);
  if (!activeP || !overP || activeId === overId || !position) return null;

  if (position === 'inside' && overP.kind !== 'folder') return null;

  const s = normalizeSchemas(schemas);
  if (activeP.kind === 'folder') {
    if (position === 'inside' && String(activeP.id) === String(overP.id)) return null;
    if (overP.kind === 'folder' && collectFolderSubtreeIds(s.folders, activeP.id).has(String(overP.id))) {
      return null;
    }
    if (position === 'inside' && isDescendantFolder(s.folders, overP.id, activeP.id)) {
      return null;
    }
  }

  return { overId, position };
}

/**
 * Move a folder/item next to (before/after) or into a target during drag-and-drop.
 * `position` is 'before' | 'after' | 'inside'. Resequences the destination siblings.
 */
export function moveSchemaEntry(schemas, { dragKind, dragId, targetKind, targetId, position }) {
  const s = normalizeSchemas(schemas);
  if (!dragId || !targetId || String(dragId) === String(targetId)) return s;

  // Resolve destination parent.
  let parentId;
  if (position === 'inside') {
    if (targetKind !== 'folder') return s; // can only drop inside folders
    parentId = String(targetId);
  } else {
    parentId =
      targetKind === 'folder'
        ? normalizeParentId(s.folders.find((f) => String(f.id) === String(targetId))?.parent_id)
        : normalizeParentId(s.items.find((it) => String(it.id) === String(targetId))?.folder_id);
  }

  // Guard: never move a folder into itself or a descendant.
  if (dragKind === 'folder' && parentId != null) {
    if (
      String(parentId) === String(dragId) ||
      isDescendantFolder(s.folders, parentId, dragId)
    ) {
      return s;
    }
  }

  // Reassign the dragged entry's parent.
  let folders = s.folders.map((f) =>
    String(f.id) === String(dragId) && dragKind === 'folder'
      ? { ...f, parent_id: parentId }
      : f
  );
  let items = s.items.map((it) =>
    String(it.id) === String(dragId) && dragKind === 'schema'
      ? { ...it, folder_id: parentId }
      : it
  );

  // Build destination sibling order (excluding the dragged entry), insert it.
  const next = { folders, items };
  const siblings = getOrderedSchemaChildren(next, parentId).filter(
    (e) => String(e.id) !== String(dragId)
  );
  let insertAt = siblings.length;
  if (position !== 'inside') {
    const idx = siblings.findIndex((e) => String(e.id) === String(targetId));
    if (idx !== -1) insertAt = position === 'before' ? idx : idx + 1;
  }
  const orderedIds = siblings.map((e) => String(e.id));
  orderedIds.splice(insertAt, 0, String(dragId));

  // Write resequenced sort_order back to both arrays.
  const orderMap = new Map(orderedIds.map((id, i) => [id, i]));
  folders = folders.map((f) =>
    orderMap.has(String(f.id)) ? { ...f, sort_order: orderMap.get(String(f.id)) } : f
  );
  items = items.map((it) =>
    orderMap.has(String(it.id)) ? { ...it, sort_order: orderMap.get(String(it.id)) } : it
  );
  return { folders, items };
}

// ---------------------------------------------------------------------------
// Node-tree edits (within a single SchemaItem's `nodes`)
// ---------------------------------------------------------------------------

/** Immutably update a node by id; `patch` may switch type and reset children. */
export function updateNode(nodes, nodeId, patch) {
  return (nodes || []).map((n) => {
    if (String(n.id) === String(nodeId)) {
      const merged = { ...n, ...patch };
      if (patch.type && !nodeHasChildren(merged)) merged.children = [];
      return merged;
    }
    return { ...n, children: updateNode(n.children, nodeId, patch) };
  });
}

export function deleteNode(nodes, nodeId) {
  return (nodes || [])
    .filter((n) => String(n.id) !== String(nodeId))
    .map((n) => ({ ...n, children: deleteNode(n.children, nodeId) }));
}

/** Insert a new empty node right after `nodeId` at the same depth. */
export function addSiblingNode(nodes, nodeId, newNode = makeSchemaNode()) {
  const out = [];
  let inserted = false;
  for (const n of nodes || []) {
    if (String(n.id) === String(nodeId)) {
      out.push({ ...n });
      out.push(newNode);
      inserted = true;
    } else {
      out.push({ ...n, children: addSiblingNode(n.children, nodeId, newNode) });
    }
  }
  return inserted ? out : out;
}

/** Append a child node under an object/array node. */
export function addChildNode(nodes, nodeId, newNode = makeSchemaNode()) {
  return (nodes || []).map((n) => {
    if (String(n.id) === String(nodeId)) {
      if (!nodeHasChildren(n)) return n;
      return { ...n, children: [...(n.children || []), newNode] };
    }
    return { ...n, children: addChildNode(n.children, nodeId, newNode) };
  });
}

/** Insert a duplicate of `nodeId` immediately after it (fresh ids for the copy subtree). */
export function duplicateNode(nodes, nodeId) {
  const dup = (list) => {
    const out = [];
    for (const n of list || []) {
      out.push(n);
      if (String(n.id) === String(nodeId)) {
        const copy = JSON.parse(JSON.stringify(n));
        const reid = (node) => {
          node.id = generateSchemaNodeId();
          if (Array.isArray(node.children)) node.children.forEach(reid);
        };
        reid(copy);
        out.push(copy);
      } else if (n.children?.length) {
        out[out.length - 1] = { ...out[out.length - 1], children: dup(n.children) };
      }
    }
    return out;
  };
  return dup(nodes);
}

/** Move a node up (-1) or down (+1) among its siblings. */
export function moveNode(nodes, nodeId, direction) {
  const shift = (list) => {
    const idx = (list || []).findIndex((n) => String(n.id) === String(nodeId));
    if (idx !== -1) {
      const nextIdx = idx + direction;
      if (nextIdx < 0 || nextIdx >= list.length) return list;
      const next = [...list];
      [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
      return next;
    }
    return (list || []).map((n) => ({ ...n, children: shift(n.children) }));
  };
  return shift(nodes);
}

/** Find a node by id anywhere in the tree (returns the node or null). */
export function findSchemaNode(nodes, nodeId) {
  for (const n of nodes || []) {
    if (String(n.id) === String(nodeId)) return n;
    const found = findSchemaNode(n.children, nodeId);
    if (found) return found;
  }
  return null;
}

/**
 * Drag-and-drop reorder for the field tree. Moves `dragId` relative to `targetId`:
 *  - 'before' | 'after' → inserts as a sibling of the target.
 *  - 'inside'           → appends as the last child of the target (object/array only).
 * Cross-level moves are allowed; a node can never be dropped into its own subtree.
 */
export function moveSchemaNodeRelative(nodes, dragId, targetId, position) {
  if (!dragId || !targetId || String(dragId) === String(targetId)) return nodes;

  const dragged = findSchemaNode(nodes, dragId);
  if (!dragged) return nodes;
  // Never drop a node into its own descendant.
  if (findSchemaNode(dragged.children, targetId)) return nodes;

  const without = deleteNode(nodes, dragId);

  if (position === 'inside') {
    const target = findSchemaNode(without, targetId);
    if (!target || !nodeHasChildren(target)) return nodes;
    return updateNode(without, targetId, {
      children: [...(target.children || []), dragged],
    });
  }

  const insert = (list) => {
    const out = [];
    for (const n of list || []) {
      if (String(n.id) === String(targetId)) {
        if (position === 'before') {
          out.push(dragged, { ...n });
        } else {
          out.push({ ...n }, dragged);
        }
      } else {
        out.push({ ...n, children: insert(n.children) });
      }
    }
    return out;
  };
  return insert(without);
}
